/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
//mmx-feature: imports --start
/*
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps, ICartlinesViewProps} from '@msdyn365-commerce-modules/cart';
*/
import MsDyn365, { getUrlSync, Image } from '@msdyn365-commerce/core';
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';
/*
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
*/
//mmx-feature: imports --end
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick, getTelemetryAttributes, TelemetryConstant, format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';

//mmx-feature: imports --start
import { PriceComponent } from '@msdyn365-commerce/components';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IProductWarranties, IWarrantyOption } from '../../../actions/DataServiceEntities.g';
import { ICartIconData } from './cart-icon.data';
import ProductWarrantyComponent from '../../../components/warranty/product-warranty/product-warranty';
import { findSelectedWarranty } from '../../../utilities/warranty/utils';
import { pairGroupedItemLines } from '../../../utilities/product/utils';
import { IGroupedItemLine, ProductLineCommerceProperties, ProductLineDataType } from '../../../utilities/utils';
import { GetProductWarrantiesByCartInput, getProductWarrntiesByCartAction } from '../../../actions/get-product-warranties-in-active-cart.action';
import { removeBuildFromCart } from '../../../utilities/cart/remove-build-from-cart';
//mmx-feature: imports --end

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
    //mmx-feature: warranty --start
    warrantiesInCart?: IProductWarranties[] | undefined;
    buildItemsToShow?: number[];
    //mmx-feature: warranty --end
}

//mmx-feature: paired cart lines --start
//moved into class
/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.-
 * @returns JSX Element.
 */
/*
const renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): JSX.Element[] | null => {
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    return cartLines.map((cartLine, index) => {
        const cartLineIndex = index;
        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLine.cartline}</Node>
                {cartLine.storeLocation}
                {cartLine.remove}
            </Node>
        );
    });
};
*/
//mmx-feature: paired cart lines --end

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);
    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {props.resources.totalPriceFormatString}
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

/**
 * Initiate subtotal message.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderSubtotalMessage = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    return cart && !cart.hasInvoiceLine ? <p className='ms-cart-icon__subtotal-message'>{props.resources.subTotalMessage}</p> : null;
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
//mmx-feature: view extension --start
/*
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>,
    ICartIconViewState
> {
*/
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<ICartIconData>,
    ICartIconViewState
> {
    //mmx-feature: view extension --end
    private isAutoDisplayTriggered: boolean;

    private readonly cartIconRef: React.RefObject<HTMLButtonElement> = React.createRef();

    //mmx-feature: view extension --start
    /*
    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}>) {
    */
    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<ICartIconData>) {
    //mmx-feature: view extension --end
        super(props);
        this.isAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false
        };
    }

    //mmx-feature --start
    /*
    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }
    }
    */
    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }
    }
    //mmx-feature --end

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    //mmx-feature: warranty --start
    public async componentDidUpdate(prevProps: Readonly<ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<ICartIconData>>, prevState: Readonly<ICartIconViewState>): Promise<void> {
        if (this.state.isModalOpen && !prevState.isModalOpen) {
            const newWarrantiesInCart = await getProductWarrntiesByCartAction(new GetProductWarrantiesByCartInput(), this.props.context.actionContext);
            this.setState({ warrantiesInCart: newWarrantiesInCart });
        }
    }
    //mmx-feature: warranty --end

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        const cart = this.props.data.cart.result ?? undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }
        //mmx-feature: show invoice --start
        let invoiceCartLine : JSX.Element = <></>; 
        if (this.props.data.cart.result?.hasInvoiceLine) {
            const invoiceLine = this.props.data.cart.result?.cart.CartLines ? this.props.data.cart.result?.cart.CartLines[0] : undefined;
            if (invoiceLine) {
                const payLoad = getPayloadObject('click', this.props.telemetryContent!, TelemetryConstant.RemoveCartItem);
                const removeCartItemAttribute = getTelemetryAttributes(this.props.telemetryContent!, payLoad);

                invoiceCartLine = <Node className='ms-cart-icon__body msc-modal__body'>
                    {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                    {this.props.checkoutBlockedDueToUnavailableFunds}
                    <Node {...this.props.CartlinesWrapper}>
                        <Node {...this.props.miniCartItemWrapper} key={0}>
                            <a className='msc-cart-line__product-title' href={`${getUrlSync(`invoice-details?invoiceId=${invoiceLine.InvoiceId}`, this.props.context.actionContext)}`}>{format(this.props.resources.invoiceLabel, invoiceLine.InvoiceId)}</a>
                            {<PriceComponent
                                data={{
                                    price: {
                                        CustomerContextualPrice: invoiceLine.Price,
                                        BasePrice: invoiceLine.InvoiceAmount
                                    }
                                }}
                                context={this.props.context}
                                id={this.props.id}
                                typeName={this.props.typeName}
                                className='msc-cart-line__product-savings-actual msc-cart-line__invoice-price'
                                originalPriceText={this.props.resources.originalPriceText}
                                currentPriceText={this.props.resources.currentPriceText}
                                salesAgreementLineId={invoiceLine.SalesAgreementLineRecordId}
                            />}
                            <Button
                                className='msc-cart-line__remove-item'
                                onClick={() => {
                                    if (this.props.data.cart.result) {
                                        const input = {
                                            cartLineIds: [invoiceLine.LineId!.toString()]
                                        };
                                        this.props.data.cart.result.removeCartLines(input).catch(error => {
                                            this.props.telemetry.warning(error);
                                            this.props.telemetry.debug('Unable to Remove Cart Line');
                                        });
                                    }
                                }}
                                title={this.props.resources.removeCartButtonText}
                                {...removeCartItemAttribute}
                            >
                                {this.props.resources.removeCartButtonText}
                            </Button>
                        </Node>
                    </Node>
                </Node>;
            }
        }
        //mmx-feature: show invoice --end
        //mmx-feature: warranty --start
        let groupedCartLines : IGroupedItemLine<IFlyoutCartLineItemViewProps>[] = [];
        if (this.state.isModalOpen && this.props.cartlines) {
            groupedCartLines = pairGroupedItemLines<IFlyoutCartLineItemViewProps>(this.props.cartlines, ProductLineDataType.IFlyoutCartLineItemViewProps);
        }
        //mmx-feature: warranty --end
        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn' ref={this.cartIconRef}>
                    {this.props.cartIcon}
                </button>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    {//mmx-feature: show invoice --start
                    this.props.data.cart.result?.hasInvoiceLine ? invoiceCartLine :
                    //mmx-feature: show invoice --end
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                        {this.props.checkoutBlockedDueToUnavailableFunds}
                        {/*mmx-feature: warranty --start*/
                        /*
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(this.props.cartlines, this.props)}</Node>
                        */
                        <Node {...this.props.CartlinesWrapper}>{this.renderGroupedCartLines(groupedCartLines, this.props, this.state.warrantiesInCart)}</Node>
                        /*mmx-feature: warranty --end*/}
                    </Node>
                    /*mmx-feature: show invoice (just the end bracket) */}
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {renderTotalPrice(this.props)}
                        {renderSubtotalMessage(this.props)}
                        {this.props.checkoutAsSignInUserButton}
                        {this.props.checkoutAsGuestButton}
                        {this.props.data.cart.result?.isEmpty ? null : this.props.goToCartButton}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: 'ms-cart-icon__minicartmodal-container',
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (): void => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.cartIconRef.current?.focus();
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
        const defaultCartItemCount: number = 0;

        const cartItemlength = props.data.cart.result ? props.data.cart.result.totalItemsInCart : defaultCartItemCount;
        const cartItemCount = `${cartItemlength} ${props.resources.items}`;

        return <Node className='ms-cart-icon__count'>{cartItemCount}</Node>;
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };

    //mmx-feature: paired cart lines --start
    private readonly renderGroupedCartLines = (
        groupedCartLines: IGroupedItemLine<IFlyoutCartLineItemViewProps>[] | undefined,
        props: ICartIconViewProps & ICartIconExtensionProps<ICartIconData>,
        productWarranties: IProductWarranties[] | undefined
    ): JSX.Element[] | null => {
        if (!groupedCartLines) {
            props.context.telemetry.error('Cartlines content is empty, module wont render');
            return null;
        }
        return groupedCartLines.map((groupedCartLine, index) => {
            const recordId = groupedCartLine.item?.data?.product?.RecordId;
            const cartLineIndex = index;
            const productWarrantiesForCartline: IProductWarranties | undefined =
                productWarranties && productWarranties.length > 0
                    ? productWarranties?.find(productWarranties => productWarranties.ProductId === groupedCartLine.item.data?.cartline.ProductId)
                    : undefined;
            let selectedWarranty: IWarrantyOption | undefined = findSelectedWarranty<IFlyoutCartLineItemViewProps>(
                recordId ?? 0,
                groupedCartLine,
                productWarrantiesForCartline,
                ProductLineDataType.IFlyoutCartLineItemViewProps
            );

            const showWarrantyForLine = productWarranties && productWarrantiesForCartline && props.context.app.config.productWarrantyEnabled;
            const showGroupedItems = groupedCartLine.buildItems;
            
            const showChildren: boolean = this.state.buildItemsToShow?.some(lineNum => lineNum === groupedCartLine.item.data?.cartline.LineNumber) ?? false;
            const toggleShowHideChildren = (buildItemsToShow?: number[], lineNumber?: number, showChildren?: boolean) => {
                if (lineNumber) {
                    if (buildItemsToShow) {
                        let updatedBuildItemsToShow: number[] | undefined = buildItemsToShow.map(lineNum => { return lineNum });
                        if (showChildren) {
                            updatedBuildItemsToShow = buildItemsToShow.filter(lineNum => lineNum !== lineNumber);
                        } else {
                            updatedBuildItemsToShow.push(lineNumber);
                        }
                        this.setState({ buildItemsToShow: updatedBuildItemsToShow });
                    } else {
                        this.setState({ buildItemsToShow: [lineNumber] });
                    }
                }
            };

            if (showGroupedItems) {
                const buildNodeProps = {
                    ...props.miniCartItemWrapper,
                    className: `${props.miniCartItemWrapper.className} build-line`
                }
                const isBuildParent = groupedCartLine.item.data?.cartline.ExtensionProperties?.find(prop => prop.Key === ProductLineCommerceProperties.isBuildParent)?.Value?.BooleanValue;
                const buildName = groupedCartLine.item.data?.cartline.ExtensionProperties?.find(prop => prop.Key === ProductLineCommerceProperties.buildParentName)?.Value?.StringValue;

                const removeBuildOnClick = isBuildParent ? () => {
                    const cart = props.data.cart.result ?? undefined;
                    const lineNumber = groupedCartLine.item.data?.cartline.LineNumber;
                    if (cart && lineNumber) {
                        removeBuildFromCart(cart, lineNumber).then(() => {
                            cart.refreshCart({});
                        });
                    }
                } : undefined;
                
                return (<Node {...buildNodeProps } key={cartLineIndex}>
                    <Node className={'msc-cart-line'}>
                        {props && <Image
                            requestContext={props.context.actionContext.requestContext}
                            src={groupedCartLine.item.data?.product?.PrimaryImageUrl ?? ''}
                            fallBackSrc={getFallbackImageUrl(
                                groupedCartLine.item.data?.product?.ItemId,
                                props.context.actionContext.requestContext.apiSettings,
                                props.context.app?.config?.OmniChannelMedia
                            )}
                            altText={groupedCartLine.item.data?.product?.Name}
                            gridSettings={props.context.request.gridSettings!}
                            imageSettings={{
                                viewports: {
                                    xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                                    sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                                    lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                                },
                                lazyload: true
                            }}
                            loadFailureBehavior='empty'
                            className={'msc-cart-line__product-image'}
                        />}
                        <h3 className='msc-cart-line__product-title'>{buildName ? `Custom Build: ${buildName}` : 'Custom Build'}</h3>
                    </Node>
                    {groupedCartLine.item.storeLocation}
                    {<div className='msc-cart-icon__build-section'>
                        <a className='msc-cart-icon__build-section__show-toggle' onClick={(e) => {
                            e.preventDefault(); 
                            toggleShowHideChildren(this.state.buildItemsToShow, groupedCartLine.item.data?.cartline.LineNumber, showChildren)
                        }}>
                            {showChildren ? 'Hide Items' : `Show Items${groupedCartLine.buildItems ? ` (${groupedCartLine.buildItems?.length} selected)` : ''}`}
                        </a>
                        {showChildren && groupedCartLine.buildItems!.map(buildItem => {
                            return (<div className='msc-cart-icon__build-section-product'>
                                {props && <Image
                                    requestContext={props.context.actionContext.requestContext}
                                    src={groupedCartLine.item.data?.product?.PrimaryImageUrl ?? ''}
                                    fallBackSrc={getFallbackImageUrl(
                                        groupedCartLine.item.data?.product?.ItemId,
                                        props.context.actionContext.requestContext.apiSettings,
                                        props.context.app?.config?.OmniChannelMedia
                                    )}
                                    altText={groupedCartLine.item.data?.product?.Name}
                                    gridSettings={props.context.request.gridSettings!}
                                    imageSettings={{
                                        viewports: {
                                            xs: { q: 'w=80&h=94&m=6', w: 0, h: 0 },
                                            sm: { q: 'w=148&h=174&m=6', w: 0, h: 0 },
                                            lg: { q: 'w=148&h=174&m=6', w: 0, h: 0 }
                                        },
                                        lazyload: true
                                    }}
                                    loadFailureBehavior='empty'
                                    className={'msc-cart-line__product-image'}
                                />}
                                <h4 className='mxc-cart-icon__product-title'>{buildItem.data?.product?.Name}</h4>
                                {props && <PriceComponent
                                    data={{
                                        price: {
                                            CustomerContextualPrice: buildItem.data?.product?.Price,
                                            BasePrice: buildItem.data?.product?.BasePrice,
                                            AdjustedPrice: buildItem.data?.product?.AdjustedPrice
                                        }
                                    }}
                                    context={props.context}
                                    id={props.id}
                                    typeName={props.typeName}
                                    className='msc-cart-icon__product-savings-actual'
                                />}
                            </div>);
                        })}
                    </div>}
                    {showWarrantyForLine && (
                        <ProductWarrantyComponent
                            key={`mmx-cartIcon-pwComponent-${index}`}
                            {...{
                                showHeader: false,
                                showIcon: false,
                                showDetailButton: false,
                                design: props.context.app.config.productWarrantySelectorDesign,
                                itemsPerRow: 4,
                                productWarranties: productWarrantiesForCartline!,
                                selectedWarranty: selectedWarranty,
                                isSelectWarrantyDisabled: true,
                                productQuantity: 1
                            }}
                        />
                    )}
                    <Button className={'msc-cart-line__remove-item msc-btn'} onClick={removeBuildOnClick}>{props.resources.removeBuildButtonText}</Button>
                </Node>);
            }

            return (
                <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                    <Node className={classnames(groupedCartLine.item.storeLocation ? 'msc-cart-line__set-net-price' : '')}>
                        {groupedCartLine.item.cartline}
                    </Node>
                    {groupedCartLine.item.storeLocation}
                    {showWarrantyForLine && (
                        <ProductWarrantyComponent
                            key={`mmx-cartIcon-pwComponent-${index}`}
                            {...{
                                showHeader: false,
                                showIcon: false,
                                showDetailButton: false,
                                design: props.context.app.config.productWarrantySelectorDesign,
                                itemsPerRow: 4,
                                productWarranties: productWarrantiesForCartline!,
                                selectedWarranty: selectedWarranty,
                                isSelectWarrantyDisabled: true,
                                productQuantity: 1
                            }}
                        />
                    )}
                    {groupedCartLine.item.remove}
                </Node>
            );
        });
    };
    //mmx-feature: paired cart lines --end
}

export default CartIconView;
